
import i18n from "@/i18n";/**
 * WashPros Policy Version Registry
 *
 *   Single source of truth for the version metadata of every policy in
 *   the WashPros Legal & Trust Policy Center. Used by:
 *     1. PolicyPageShell  → renders the "Policy version / Effective date /
 *                            Draft status / Contact" block on every page.
 *     2. LegalHubPage     → shows the same draft-status badge per card.
 *     3. AdminPolicyVersionsPage (admin-only) → tabular registry view.
 *     4. policyVersionSnapshot.js → builds the snapshot we attach to
 *        vendor onboarding, contractor onboarding, checkout, and review
 *        submission payloads so the backend can record exactly which
 *        policy versions a user agreed to.
 *
 *   This module purposely contains ONLY metadata — no policy body copy.
 *   Body copy lives in policyContent.js. The two are kept in lock-step
 *   by `policy_id`.
 *
 *   IMPORTANT: The policies are pending formal legal-counsel review.
 *   The "draft_status" field below is the canonical wording you should
 *   surface to end users until counsel signs off. Do NOT display
 *   "Last reviewed by legal" anywhere yet.
 */

const COMMON = {
  effective_date: "May 16, 2026",
  get draft_status() { return i18n.t("legal.pending_legal_counsel_review"); },
  contact_email: "legal@washpros.io",
  // No previous version yet — these are all v1.0 first drafts.
  previous_version: null,
};

// Helper: produce a tiny stable hash from a string. Used so backend
// records can be cross-referenced to the exact policy text the user
// saw at the moment of agreement. Implementation is dependency-free
// and deterministic — same input always produces the same hash.
const cheapHash = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  }
  return `h${h.toString(16).padStart(8, "0")}`;
};

// Stable content stamp: keyed off (id + version + effective_date) so
// the hash changes whenever any of those change. The full policy body
// can be hashed in the future by passing it through cheapHash too —
// kept light for now to avoid coupling this registry to the long
// content module.
const stamp = (id, version) =>
  cheapHash([id, version, COMMON.effective_date].join("|"));

const v1 = (id, title, route) => ({
  policy_id: id,
  title,
  route,
  version: "1.0",
  effective_date: COMMON.effective_date,
  draft_status: COMMON.draft_status,
  contact_email: COMMON.contact_email,
  previous_version: COMMON.previous_version,
  content_hash: stamp(id, "1.0"),
});

export const POLICY_REGISTRY = [
  // Existing policies (left untouched in this milestone but tracked
  // here so the agreement-capture snapshot is complete).
  v1("terms", i18n.t("legal.terms_of_service"), "/terms"),
  v1("privacy", i18n.t("legal.privacy_policy"), "/privacy"),
  v1("copyright-policy", i18n.t("legal.copyright_notification_policy"), "/copyright-policy"),

  // 16 new policies introduced 2026-05-16 (post Florida DBA filing).
  v1("acceptable-use", i18n.t("legal.acceptable_use_policy"), "/acceptable-use"),
  v1("vendor-marketplace-standards", i18n.t("legal.vendor_marketplace_standards"), "/vendor-marketplace-standards"),
  v1("booking-refund-dispute-policy", i18n.t("legal.booking_refund_dispute_policy"), "/booking-refund-dispute-policy"),
  v1("communications-policy", i18n.t("legal.communications_policy"), "/communications-policy"),
  v1("data-security", i18n.t("legal.data_security_summary"), "/data-security"),
  v1("cookie-policy", i18n.t("legal.cookie_policy"), "/cookie-policy"),
  v1("data-retention", i18n.t("legal.data_retention_policy"), "/data-retention"),
  v1("reviews-policy", i18n.t("legal.reviews_policy"), "/reviews-policy"),
  v1("influencer-disclosure", i18n.t("legal.influencer_affiliate_disclosure_policy"), "/influencer-disclosure"),
  v1("trademark-policy", i18n.t("legal.trademark_policy"), "/trademark-policy"),
  v1("subscription-terms", i18n.t("legal.subscription_terms"), "/subscription-terms"),
  v1("vendor-payout-policy", i18n.t("legal.vendor_payout_policy"), "/vendor-payout-policy"),
  v1("ai-disclosure", i18n.t("legal.ai_disclosure"), "/ai-disclosure"),
  v1("photo-media-policy", i18n.t("legal.photo_media_policy"), "/photo-media-policy"),
  v1("property-damage-policy", i18n.t("legal.property_damage_policy"), "/property-damage-policy"),
  v1("dmca-agent", i18n.t("legal.dmca_designated_agent"), "/dmca-agent"),
];

// Quick lookup helper
export const policyById = (id) =>
  POLICY_REGISTRY.find((p) => p.policy_id === id) || null;

// Surface keyword bundles — used when capturing agreement for a
// specific user-facing context. Each bundle is just an ordered list of
// policy ids; the snapshot helper resolves them to full registry rows.
export const POLICY_BUNDLES = {
  vendor_onboarding: [
    "terms",
    "privacy",
    "copyright-policy",
    "vendor-marketplace-standards",
    "vendor-payout-policy",
    "communications-policy",
    "photo-media-policy",
    "property-damage-policy",
    "acceptable-use",
  ],
  contractor_onboarding: [
    "terms",
    "privacy",
    "copyright-policy",
    "vendor-marketplace-standards",
    "vendor-payout-policy",
    "communications-policy",
    "photo-media-policy",
    "property-damage-policy",
    "acceptable-use",
  ],
  customer_checkout: [
    "terms",
    "privacy",
    "booking-refund-dispute-policy",
    "communications-policy",
    "ai-disclosure",
    "property-damage-policy",
  ],
  vip_booking: [
    "terms",
    "privacy",
    "subscription-terms",
    "booking-refund-dispute-policy",
  ],
  review_submission: ["reviews-policy"],
};
